import {useState} from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import {Person as PersonIcon} from "@mui/icons-material";
import {
    createPersonalMeasurement,
    updatePersonalMeasurement,
    type TraineeMeasurementItem,
} from "../../../services/TraineeMeasurementService";
import {UNIT_OPTIONS} from "../../measurements/measurementUnits";
// @ts-ignore — untyped JS helper (TODO(ts-migration): convert CacheRtl.js)
import {RTL} from "../../../cache/CacheRtl";
import {useMessageContext} from "../../../contexts/MessageContext";
// @ts-ignore — untyped JS design tokens (TODO(ts-migration): convert coachTokens.js)
import {t} from "../../dashboard/coach/coachTokens";

interface Props {
    open: boolean;
    traineeId: number;
    /** null = create; otherwise edit this personal measurement. */
    item: TraineeMeasurementItem | null;
    onClose: () => void;
    onSaved: () => void;
}

/**
 * Create / edit a PERSONAL measurement for one trainee.
 *
 * A dialog rather than a reuse of MeasurementDefinitionPage: that screen is a full route with its
 * own back-nav, delete button, and a gender selector — and gender is meaningless for a measurement
 * made for one specific person.
 *
 * ## No translation panel here, deliberately — and the English field is gone
 *
 * This writes a `BodyMeasurementDefinitionEntity` with `trainee` set (see
 * `BodyMeasurementTraineeService.createPersonal`), so it technically IS a registered
 * MEASUREMENT_DEFINITION row and `@authz.canTranslate` would accept it. It still must not get the
 * panel: this is **one coach writing for one specific trainee** — the category the i18n design
 * explicitly does not translate, because there is no translator in the loop and never will be.
 * Offering a 5-language editor here would create a permanently-empty grid and a product promise
 * nobody can keep. A coach whose client reads Arabic types the name in Arabic, and the column is
 * just text.
 *
 * So the `שם באנגלית` input was REMOVED rather than replaced. The company catalog
 * (MeasurementDefinitionPage) is the one that hosts the panel.
 *
 * `<RTL>` goes INSIDE `<Dialog>` (Portal escapes the page's RTL cache).
 */
const PersonalMeasurementDialog = ({open, traineeId, item, onClose, onSaved}: Props) => {
    const notify = useMessageContext()?.notificationHandler;
    const isEdit = Boolean(item);

    const [nameHeb, setNameHeb] = useState(item?.nameHeb || "");
    const [unit, setUnit] = useState(item?.unit || "cm");
    const [descriptionHeb, setDescriptionHeb] = useState(item?.descriptionHeb || "");
    const [busy, setBusy] = useState(false);

    const handleSave = async () => {
        const trimmedHeb = nameHeb.trim();
        if (!trimmedHeb) {
            notify?.error("יש להזין שם למדד");
            return;
        }

        const payload = {
            // `name` is @NotBlank on the server and is the column the uniqueness check runs on, so
            // it mirrors the one name the user typed. There is no second language to reconcile.
            name: trimmedHeb,
            nameHeb: trimmedHeb,
            unit,
            descriptionHeb: descriptionHeb.trim() || undefined,
        };

        setBusy(true);
        const res = isEdit
            ? await updatePersonalMeasurement(traineeId, item!.id as number, payload)
            : await createPersonalMeasurement(traineeId, payload);
        setBusy(false);

        if (res.success) {
            if (res.message) notify?.success(res.message);
            onSaved();
        } else {
            notify?.error(res.message || "אירעה שגיאה");
        }
    };

    const field = {
        "& .MuiOutlinedInput-root": {borderRadius: t.rs, fontSize: "13px"},
        "& .MuiInputLabel-root": {fontSize: "13px"},
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
                PaperProps={{sx: {borderRadius: t.r, p: "20px"}}}>
            <RTL>
                <Box dir="rtl" sx={{display: "flex", flexDirection: "column", gap: "14px"}}>
                    <Box sx={{display: "flex", alignItems: "center", gap: "10px"}}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: t.rs, flexShrink: 0,
                            background: t.greenLight, color: t.greenText,
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <PersonIcon sx={{fontSize: 19}}/>
                        </Box>
                        <Box>
                            <Typography sx={{fontSize: "15px", fontWeight: 700, color: t.text}}>
                                {isEdit ? "עריכת מדד אישי" : "מדד אישי חדש"}
                            </Typography>
                            <Typography sx={{fontSize: "11.5px", color: t.t3}}>
                                המדד יופיע רק אצל מתאמן זה
                            </Typography>
                        </Box>
                    </Box>

                    <TextField
                        label="שם המדד"
                        value={nameHeb}
                        onChange={(e) => setNameHeb(e.target.value)}
                        size="small"
                        autoFocus
                        fullWidth
                        sx={field}
                    />

                    <TextField
                        label="יחידת מידה"
                        value={unit}
                        onChange={(e) => setUnit(e.target.value)}
                        select
                        size="small"
                        fullWidth
                        sx={field}
                    >
                        {UNIT_OPTIONS.map((u) => (
                            <MenuItem key={u.value} value={u.value} sx={{fontSize: "13px"}}>
                                {u.label}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="הוראות מדידה (רשות)"
                        value={descriptionHeb}
                        onChange={(e) => setDescriptionHeb(e.target.value)}
                        size="small"
                        multiline
                        minRows={2}
                        fullWidth
                        sx={field}
                    />

                    <Box sx={{display: "flex", gap: "8px", justifyContent: "flex-start", mt: "2px"}}>
                        <Box
                            component="button"
                            onClick={handleSave}
                            disabled={busy}
                            sx={{
                                display: "inline-flex", alignItems: "center", gap: "6px",
                                height: 36, px: "16px", borderRadius: t.rs,
                                background: t.green, color: "#fff", border: "none",
                                fontSize: "13px", fontWeight: 700, fontFamily: "inherit",
                                cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1,
                                transition: "all .14s",
                                "&:hover": {background: busy ? t.green : t.greenDark},
                            }}
                        >
                            {busy && <CircularProgress size={13} sx={{color: "#fff"}}/>}
                            שמור
                        </Box>
                        <Box
                            component="button"
                            onClick={onClose}
                            sx={{
                                height: 36, px: "14px", borderRadius: t.rs,
                                background: t.white, color: t.t2, border: `1px solid ${t.border}`,
                                fontSize: "13px", fontWeight: 600, fontFamily: "inherit",
                                cursor: "pointer", transition: "all .14s",
                                "&:hover": {background: t.bg},
                            }}
                        >
                            ביטול
                        </Box>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default PersonalMeasurementDialog;
